import Button from 'antd/lib/button';
import Input from 'antd/lib/input';
import message from 'antd/lib/message';
import Model from 'antd/lib/modal';
import Row from 'antd/lib/row';
import Select from 'antd/lib/select';
import Steps from 'antd/lib/steps';
import React, { useState, useContext, useEffect } from 'react';
import { axiosGetCall, axiosPostCall } from '../api/axiosCall';
import { API_END_POINT, SOMETHING_WENT_WRONG } from '../constant/constants';
import { AuthContext } from '../context/AuthProvider';

const { Option } = Select;
const { Step } = Steps;

function AddUserModal({ isOpen, callback, handleAddUserModalCloseClick }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    mobileNo: '',
    gstNumber: '',
    email: '',
    password: '',
    salesPerson: '',
    priceList: [],
    createdBy: 'Admin', // Default value for admin-created users
  });
  const [priceCategoryList, setPriceCategoryList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const [isCheckingGST, setIsCheckingGST] = useState(false);
  const [isCompanyNameDisabled, setIsCompanyNameDisabled] = useState(false);
  const { token } = useContext(AuthContext);

  // Fetch price category list when modal opens
  useEffect(() => {
    if (isOpen) {
      getPriceCategoryList();
    }
  }, [isOpen]);

  async function getPriceCategoryList() {
    try {
      const res = await axiosGetCall({
        apiEndPoint: API_END_POINT.GET_PRODUCT_PRICE_CATEGORY,
        header: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.hideErrorFromUser) {
        setPriceCategoryList(res.data);
      } else {
        message.error(res.error.message);
      }
    } catch (error) {
      console.error(error);
      message.error(SOMETHING_WENT_WRONG);
    }
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      fullName: '',
      companyName: '',
      mobileNo: '',
      gstNumber: '',
      email: '',
      password: '',
      salesPerson: '',
      priceList: [],
      createdBy: 'Admin',
    });
    setCurrentStep(0);
    setIsCompanyNameDisabled(false);
  };

  const handleClose = () => {
    resetForm();
    handleAddUserModalCloseClick();
  };

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const checkPhoneNumberExists = async (phoneNumber) => {
    try {
      setIsCheckingPhone(true);
      const res = await axiosPostCall({
        apiEndPoint: `${API_END_POINT.CHECK_MOBILE_NUMBER_EXISTS}?phoneNumber=${phoneNumber}`,
        header: {
          Authorization: `Bearer ${token}`,
        },
        body: {},
      });

      // If hideErrorFromUser is false, it means mobile number doesn't exist (good)
      // If hideErrorFromUser is true, it means mobile number exists (bad)
      return res.hideErrorFromUser;
    } catch (error) {
      console.error('Error checking phone number:', error);
      message.error('Failed to check phone number');
      return false;
    } finally {
      setIsCheckingPhone(false);
    }
  };

  // GST number validation regex
  const validateGSTNumber = (gstNumber) => {
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{3}$/;
    console.log(gstRegex.test(gstNumber), gstNumber);
    return gstRegex.test(gstNumber);
  };

  const checkGSTDetails = async (gstNumber) => {
    if (!validateGSTNumber(gstNumber)) {
      message.error('Please enter a valid GST number');
      setIsCompanyNameDisabled(false);
      return;
    }

    try {
      setIsCheckingGST(true);
      const res = await axiosPostCall({
        apiEndPoint: `${API_END_POINT.GET_GST_NUMBER_DETAILS}?gstNo=${gstNumber}`,
        header: {
          Authorization: `Bearer ${token}`,
        },
        body: {},
      });

      if (res.hideErrorFromUser && res.data) {
        // GST found - auto-populate company name and disable input
        setFormData((prev) => ({
          ...prev,
          companyName: res.data,
        }));
        setIsCompanyNameDisabled(true);
        message.success('Company name auto-populated from GST details');
      } else {
        // GST not found - enable company name input
        setFormData((prev) => ({
          ...prev,
          companyName: '',
        }));
        setIsCompanyNameDisabled(false);
        message.info(
          'GST number not found. Please enter company name manually.',
        );
      }
    } catch (error) {
      console.error('Error checking GST details:', error);
      message.error('Failed to fetch GST details');
      setIsCompanyNameDisabled(false);
    } finally {
      setIsCheckingGST(false);
    }
  };

  const handleGSTChange = (value) => {
    handleInputChange('gstNumber', value);
    // Clear company name and enable input when GST changes
    setFormData((prev) => ({
      ...prev,
      companyName: '',
    }));
    setIsCompanyNameDisabled(false);
  };

  const validateStep1 = async () => {
    if (!formData.fullName.trim()) {
      message.error('Please enter full name');
      return false;
    }
    if (!formData.mobileNo.trim()) {
      message.error('Please enter mobile number');
      return false;
    }

    // Check if phone number already exists
    const phoneExists = await checkPhoneNumberExists(formData.mobileNo.trim());
    if (phoneExists) {
      message.error(
        'This mobile number is already registered. Please use a different number.',
      );
      return false;
    }

    return true;
  };

  const validateStep2 = () => {
    if (!formData.companyName.trim()) {
      message.error('Please enter company name');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.salesPerson.trim()) {
      message.error('Please enter sales person name');
      return false;
    }
    if (formData.priceList.length === 0) {
      message.error('Please select at least one price list');
      return false;
    }
    return true;
  };

  async function createUser() {
    try {
      setIsLoading(true);
      const res = await axiosPostCall({
        apiEndPoint: API_END_POINT.REGISTER_COMPANY_FROM_ADMIN,
        header: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: {
          gstNumber: formData.gstNumber.trim(),
          companyName: formData.companyName.trim(),
          fullName: formData.fullName.trim(),
          phoneNumber: formData.mobileNo.trim(),
          isApproved: true,
          salesman: formData.salesPerson.trim(),
          createdBy: formData.createdBy,
          productPriceCategories: formData.priceList,
        },
      });

      if (res?.hideErrorFromUser) {
        message.success('User registered successfully');
        callback(); // This will refresh the user list
        handleClose();
        return;
      } else {
        message.error(res?.error?.message || 'Failed to register user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      message.error(SOMETHING_WENT_WRONG);
    } finally {
      setIsLoading(false);
    }
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div className='flex flex-col space-y-4'>
            <Row className='font-bold text-xl self-center'>
              Step 1: Personal Information
            </Row>

            <div className='flex flex-col space-y-2'>
              <label className='font-medium'>Full Name *</label>
              <Input
                placeholder='Enter full name'
                value={formData.fullName}
                onChange={(e) => handleInputChange('fullName', e.target.value)}
              />
            </div>

            <div className='flex flex-col space-y-2'>
              <label className='font-medium'>Mobile Number *</label>
              <Input
                placeholder='Enter mobile number'
                value={formData.mobileNo}
                onChange={(e) => handleInputChange('mobileNo', e.target.value)}
                suffix={isCheckingPhone ? 'Checking...' : null}
                disabled={isCheckingPhone}
              />
            </div>
          </div>
        );

      case 1:
        return (
          <div className='flex flex-col space-y-4'>
            <Row className='font-bold text-xl self-center'>
              Step 2: Company Information
            </Row>

            <div className='flex flex-col space-y-2'>
              <label className='font-medium'>GST Number</label>
              <Input
                placeholder='Enter GST number (e.g., 22ABCDE1234F1A2B)'
                value={formData.gstNumber}
                onChange={(e) =>
                  handleGSTChange(e.target.value.trim().toUpperCase())
                }
                onBlur={(e) => {
                  if (e.target.value.trim()) {
                    checkGSTDetails(e.target.value.trim());
                  }
                }}
                suffix={isCheckingGST ? 'Checking...' : null}
                disabled={isCheckingGST}
                style={{ textTransform: 'uppercase' }}
              />
            </div>

            <div className='flex flex-col space-y-2'>
              <label className='font-medium'>Company Name *</label>
              <Input
                placeholder='Enter company name'
                value={formData.companyName}
                onChange={(e) =>
                  handleInputChange('companyName', e.target.value)
                }
                disabled={isCompanyNameDisabled}
                style={{
                  backgroundColor: isCompanyNameDisabled ? '#f5f5f5' : 'white',
                  cursor: isCompanyNameDisabled ? 'not-allowed' : 'text',
                }}
              />
              {isCompanyNameDisabled && (
                <p className='text-xs text-green-600'>
                  ✓ Company name auto-populated from GST details
                </p>
              )}
            </div>
          </div>
        );

      case 2:
        return (
          <div className='flex flex-col space-y-4'>
            <Row className='font-bold text-xl self-center'>
              Step 3: Sales & Pricing
            </Row>

            <div className='flex flex-col space-y-2'>
              <label className='font-medium'>Sales Person Name *</label>
              <Input
                placeholder='Enter sales person name'
                value={formData.salesPerson}
                onChange={(e) =>
                  handleInputChange('salesPerson', e.target.value)
                }
              />
            </div>

            <div className='flex flex-col space-y-2'>
              <label className='font-medium'>Price List *</label>
              <Select
                placeholder='Select price list'
                showSearch
                mode='multiple'
                onChange={(value) => handleInputChange('priceList', value)}
                value={formData.priceList}
                className='w-full'
              >
                {priceCategoryList.map((priceCategory) => (
                  <Option
                    key={priceCategory.priceCategoryId}
                    value={priceCategory.priceCategoryId}
                  >
                    {priceCategory.priceCategoryDescription}
                  </Option>
                ))}
              </Select>
            </div>
          </div>
        );

      case 3:
        return (
          <div className='flex flex-col space-y-4'>
            <Row className='font-bold text-xl self-center'>Review Details</Row>

            <div className='bg-gray-50 p-4 rounded-lg space-y-3'>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='font-medium text-gray-600'>
                    Full Name:
                  </label>
                  <p className='text-gray-800'>{formData.fullName}</p>
                </div>
                <div>
                  <label className='font-medium text-gray-600'>
                    Mobile Number:
                  </label>
                  <p className='text-gray-800'>{formData.mobileNo}</p>
                </div>
                <div>
                  <label className='font-medium text-gray-600'>
                    Company Name:
                  </label>
                  <p className='text-gray-800'>{formData.companyName}</p>
                </div>
                <div>
                  <label className='font-medium text-gray-600'>
                    GST Number:
                  </label>
                  <p className='text-gray-800'>
                    {formData.gstNumber || 'Not provided'}
                  </p>
                </div>
                <div>
                  <label className='font-medium text-gray-600'>
                    Sales Person:
                  </label>
                  <p className='text-gray-800'>{formData.salesPerson}</p>
                </div>
                <div>
                  <label className='font-medium text-gray-600'>
                    Price List:
                  </label>
                  <div className='text-gray-800'>
                    {formData.priceList.map((id) => {
                      const category = priceCategoryList.find(
                        (cat) => cat.priceCategoryId === id,
                      );
                      return (
                        <span
                          key={id}
                          className='block text-sm'
                        >
                          {category?.priceCategoryDescription}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <Model
        open={isOpen}
        onCancel={handleClose}
        closable={false}
        footer={null}
        width={700}
      >
        <div className='flex flex-col space-y-6'>
          <Row className='font-bold text-2xl self-center'>Add New User</Row>

          {/* Steps */}
          <Steps
            current={currentStep}
            className='mb-6'
          >
            <Step title='Personal Info' />
            <Step title='Company Info' />
            <Step title='Sales & Pricing' />
            <Step title='Review' />
          </Steps>

          {/* Step Content */}
          <div className='min-h-[300px]'>{renderStepContent()}</div>

          {/* Navigation Buttons */}
          <div className='flex flex-row justify-between pt-4 border-t'>
            <div>
              {currentStep > 0 && (
                <Button
                  onClick={prevStep}
                  className='w-28 bg-surface border border-border text-text hover:text-primary hover:border-primary'
                  disabled={isLoading}
                >
                  Previous
                </Button>
              )}
            </div>

            <div className='flex space-x-2'>
              <Button
                onClick={handleClose}
                className='w-28 bg-surface border border-border text-text hover:text-primary hover:border-primary'
                disabled={isLoading}
              >
                Cancel
              </Button>

              {currentStep < 3 ? (
                <Button
                  onClick={async () => {
                    if (currentStep === 0) {
                      const isValid = await validateStep1();
                      if (isValid) {
                        nextStep();
                      }
                    } else if (currentStep === 1 && validateStep2()) {
                      nextStep();
                    } else if (currentStep === 2 && validateStep3()) {
                      nextStep();
                    }
                  }}
                  type='primary'
                  className='w-28 border-none bg-gradient-to-r from-blue-500 to-purple-600 shadow hover:shadow-md'
                  disabled={isLoading || isCheckingPhone || isCheckingGST}
                  loading={
                    (currentStep === 0 && isCheckingPhone) ||
                    (currentStep === 1 && isCheckingGST)
                  }
                >
                  {currentStep === 0 && isCheckingPhone
                    ? 'Checking...'
                    : currentStep === 1 && isCheckingGST
                      ? 'Checking...'
                      : 'Next'}
                </Button>
              ) : (
                <Button
                  onClick={createUser}
                  type='primary'
                  className='w-36 border-none bg-gradient-to-r from-blue-500 to-purple-600 shadow hover:shadow-md'
                  disabled={isLoading}
                >
                  {isLoading ? 'Creating...' : 'Create User'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Model>
    </div>
  );
}

export default AddUserModal;
